import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { updateContactInfo, validatePhoneNumber, validateStateCode, formatPhoneNumber } from '../services/profileService';
import './MobileProfileButton.css';

/**
 * Mobile Profile Button Component
 *
 * Displays user avatar in mobile header with status-colored border.
 * Expands to show full profile information on click.
 * Supports inline editing of contact information.
 * Mobile-only component (hidden on desktop).
 */
const MobileProfileButton = ({ user, memberInfo, onSignOut }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  // Edit form state
  const [editPhone, setEditPhone] = useState('');
  const [editWhatsapp, setEditWhatsapp] = useState('');
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(false);
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  useEffect(() => {
    // Click outside to close (but not if editing)
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !isEditing) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded, isEditing]);

  const loadProfileData = async () => {
    try {
      const { data, error } = await supabase
        .from('verified_users')
        .select('*, approval_status')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        setProfileData(data);

        // Calculate days remaining for provisional status
        if (data.provisional_until) {
          const daysLeft = Math.ceil(
            (new Date(data.provisional_until) - new Date()) / (1000 * 60 * 60 * 24)
          );
          setDaysRemaining(Math.max(0, daysLeft));
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleEditClick = () => {
    // Initialize edit form with current values
    setEditPhone(profileData?.phone_number || '');
    setEditWhatsapp(profileData?.whatsapp_number || '');
    setEditCity(profileData?.city || '');
    setEditState(profileData?.state || '');
    setWhatsappSameAsPhone(profileData?.whatsapp_number === profileData?.phone_number);
    setError('');
    setSaveSuccess(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    setSaveSuccess(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSaveSuccess(false);

    // Validation
    if (!validatePhoneNumber(editPhone)) {
      setError('Invalid phone number format');
      return;
    }

    if (!validatePhoneNumber(editWhatsapp) && editWhatsapp) {
      setError('Invalid WhatsApp number format');
      return;
    }

    if (!validateStateCode(editState) && editState) {
      setError('State must be a 2-character code');
      return;
    }

    setIsSaving(true);

    try {
      // Determine WhatsApp number (use phone if checkbox is checked)
      const finalWhatsapp = whatsappSameAsPhone ? editPhone : editWhatsapp;

      await updateContactInfo(user.id, {
        phoneNumber: editPhone,
        whatsappNumber: finalWhatsapp,
        city: editCity,
        state: editState.toUpperCase()
      });

      // Reload profile data
      await loadProfileData();

      // Show success
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setIsEditing(false);
      }, 1500);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleWhatsappCheckboxChange = (checked) => {
    setWhatsappSameAsPhone(checked);
    if (checked) {
      setEditWhatsapp(editPhone);
    }
  };

  if (!user) return null;

  const getInitials = () => {
    if (profileData?.first_name && profileData?.last_name) {
      return `${profileData.first_name[0]}${profileData.last_name[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getStatusColor = () => {
    if (!profileData) return 'incomplete';

    // Check approval_status first - this determines if admin has approved the user
    if (profileData.approval_status === 'admin_approved' ||
        profileData.approval_status === 'auto_approved' ||
        profileData.approval_status === 'call_verified') {
      return 'approved';  // GREEN BADGE - Admin approved
    }

    // Check verification_status for provisional/email-verified users
    if (profileData.verification_status === 'email_verified' ||
        profileData.verification_status === 'provisional') {
      return 'provisional';  // YELLOW/ORANGE - Pending admin approval
    }

    // Legacy verification_status values
    if (profileData.verification_status === 'approved' ||
        profileData.verification_status === 'verified') {
      return 'approved';
    }

    if (profileData.verification_status === 'restricted' ||
        profileData.verification_status === 'blocked') {
      return 'restricted';
    }

    return 'incomplete';
  };

  const getStatusText = () => {
    if (!profileData) return 'Profile Incomplete';

    // Admin approved = fully verified (GREEN BADGE)
    if (profileData.approval_status === 'admin_approved' ||
        profileData.approval_status === 'auto_approved' ||
        profileData.approval_status === 'call_verified') {
      return 'Verified Member';
    }

    // Email verified but not admin approved = provisional (YELLOW BADGE)
    if (profileData.verification_status === 'email_verified') {
      return 'Provisional - Pending Approval';
    }

    // Provisional period with countdown
    if (profileData.verification_status === 'provisional') {
      return `Provisional (${daysRemaining}d left)`;
    }

    // Legacy or other statuses
    if (profileData.verification_status === 'approved') {
      return 'Verified Member';
    }

    if (profileData.verification_status === 'restricted') {
      return 'Restricted Access';
    }

    if (profileData.verification_status === 'blocked') {
      return 'Account Blocked';
    }

    return 'Pending Verification';
  };

  const statusColor = getStatusColor();

  return (
    <div className="mobile-profile-button" ref={dropdownRef}>
      <button
        className={`profile-avatar status-${statusColor}`}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label="Profile menu"
      >
        <span className="avatar-initials">{getInitials()}</span>
      </button>

      {isExpanded && (
        <div className="mobile-profile-dropdown">
          <div className="dropdown-header">
            <div className="dropdown-avatar-large">
              {getInitials()}
            </div>
            <div className="dropdown-info">
              <h3 className="dropdown-name">
                {profileData?.first_name || 'User'} {profileData?.last_name || ''}
              </h3>
              <p className="dropdown-email">
                {user.email} {!isEditing && <span className="lock-icon">🔒</span>}
              </p>
            </div>
          </div>

          <div className="dropdown-divider"></div>

          {!isEditing ? (
            // Display Mode
            <>
              <div className="dropdown-section">
                <div className="status-row">
                  <span className="status-label">Status</span>
                  <span className={`status-badge status-${statusColor}`}>
                    <span className="status-dot"></span>
                    {getStatusText()}
                  </span>
                </div>

                <div className="info-row">
                  <span className="info-label">📧 Email</span>
                  <span className="info-value email-read-only">
                    {user.email} <span className="lock-icon">🔒</span>
                  </span>
                </div>

                {profileData?.phone_number && (
                  <div className="info-row">
                    <span className="info-label">📱 Phone</span>
                    <span className="info-value">{formatPhoneNumber(profileData.phone_number)}</span>
                  </div>
                )}

                {profileData?.whatsapp_number && (
                  <div className="info-row">
                    <span className="info-label">💬 WhatsApp</span>
                    <span className="info-value">{formatPhoneNumber(profileData.whatsapp_number)}</span>
                  </div>
                )}

                {profileData?.city && profileData?.state && (
                  <div className="info-row">
                    <span className="info-label">📍 Location</span>
                    <span className="info-value">{profileData.city}, {profileData.state}</span>
                  </div>
                )}

                {profileData?.verification_status === 'provisional' && daysRemaining !== null && (
                  <div className="warning-box">
                    <div className="warning-icon">⏰</div>
                    <div className="warning-text">
                      <strong>{daysRemaining} days</strong> left to verify email
                    </div>
                  </div>
                )}

                {profileData?.verification_status === 'email_verified' && profileData?.approval_status === 'pending' && (
                  <div className="info-box">
                    <div className="info-icon">⏳</div>
                    <div className="info-text">
                      Profile pending community approval
                    </div>
                  </div>
                )}
              </div>

              <div className="dropdown-divider"></div>

              <div className="dropdown-actions">
                <button className="dropdown-btn edit-btn" onClick={handleEditClick}>
                  ✏️ Edit Profile
                </button>
                <button className="dropdown-btn" onClick={() => setIsExpanded(false)}>
                  Close
                </button>
                <button className="dropdown-btn sign-out-btn" onClick={onSignOut}>
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            // Premium Edit Mode
            <>
              <div className="edit-mode-header">
                <h3 className="edit-mode-title">
                  ✏️ Edit Profile
                </h3>
                <button
                  type="button"
                  className="edit-mode-close"
                  onClick={handleCancelEdit}
                  aria-label="Close edit mode"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSaveProfile} className="dropdown-edit-form">
                <div className="dropdown-section">
                  {/* Contact Information Group */}
                  <div className="field-group">
                    <h4 className="field-group-title">📧 Contact Information</h4>

                    {/* Phone Number - Horizontal Layout */}
                    <div className="form-group-horizontal">
                      <label className="edit-label-horizontal">
                        📱 Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={editPhone}
                        onChange={(e) => {
                          setEditPhone(e.target.value);
                          if (whatsappSameAsPhone) {
                            setEditWhatsapp(e.target.value);
                          }
                        }}
                        placeholder="+1 (555) 123-4567"
                        required
                        className="edit-input-no-icon"
                      />
                    </div>

                    {/* WhatsApp Number - Horizontal Layout */}
                    <div className="form-group-horizontal">
                      <label className="edit-label-horizontal">
                        💬 WhatsApp Number
                      </label>
                      <input
                        type="tel"
                        value={editWhatsapp}
                        onChange={(e) => setEditWhatsapp(e.target.value)}
                        placeholder="+1 (555) 987-6543"
                        disabled={whatsappSameAsPhone}
                        className="edit-input-no-icon"
                      />
                    </div>

                    {/* Premium Toggle Switch */}
                    <div className={`toggle-container ${whatsappSameAsPhone ? 'active' : ''}`}>
                      <label className="toggle-label" onClick={() => handleWhatsappCheckboxChange(!whatsappSameAsPhone)}>
                        Same as phone number
                      </label>
                      <div
                        className={`toggle-switch ${whatsappSameAsPhone ? 'active' : ''}`}
                        onClick={() => handleWhatsappCheckboxChange(!whatsappSameAsPhone)}
                      >
                        <div className="toggle-slider"></div>
                      </div>
                    </div>
                  </div>

                  {/* Location Group */}
                  <div className="field-group">
                    <h4 className="field-group-title">📍 Location</h4>

                    {/* City - Horizontal Layout */}
                    <div className="form-group-horizontal">
                      <label className="edit-label-horizontal">
                        🏙️ City
                      </label>
                      <input
                        type="text"
                        value={editCity}
                        onChange={(e) => setEditCity(e.target.value)}
                        placeholder="San Francisco"
                        className="edit-input-no-icon"
                      />
                    </div>

                    {/* State - Horizontal Layout */}
                    <div className="form-group-horizontal">
                      <label className="edit-label-horizontal">
                        📌 State
                      </label>
                      <input
                        type="text"
                        value={editState}
                        onChange={(e) => setEditState(e.target.value.slice(0, 2).toUpperCase())}
                        placeholder="CA"
                        maxLength="2"
                        className="edit-input-no-icon"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="edit-error">
                      {error}
                    </div>
                  )}

                  {/* Success Message */}
                  {saveSuccess && (
                    <div className="edit-success">
                      Profile updated successfully!
                    </div>
                  )}
                </div>

                <div className="dropdown-divider"></div>

                {/* Action Buttons */}
                <div className="dropdown-actions">
                  <button
                    type="button"
                    className="dropdown-btn"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`dropdown-btn save-btn ${isSaving ? 'loading' : ''}`}
                    disabled={isSaving}
                  >
                    <span>{isSaving ? 'Saving...' : '💾 Save Changes'}</span>
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileProfileButton;
